// Namespace import (not a default import): @intercom/messenger-js-sdk is a CJS
// module whose default export Vite 8 / Rolldown mis-interops in the production
// build — a default import compiled to `(0, sdk.default)(...)` and threw
// "is not a function" in prod (dev's esbuild pre-bundling masked it). The SDK
// also exposes everything as named CJS exports (exports.Intercom/shutdown/
// update), which resolve reliably regardless of how the bundler synthesizes the
// default, so we bind from those (with a `.default` fallback for the callable).
import * as IntercomSDK from '@intercom/messenger-js-sdk'
import { isPreviewMode } from '@/assetSource'
import { useAuthStore } from '@/stores/auth'

const Intercom = IntercomSDK.Intercom ?? IntercomSDK.default
const { shutdown, update } = IntercomSDK

const INTERCOM_APP_ID = 'a313kgje'
const INTERCOM_REGION = 'eu'

const isDevelopment = import.meta.env.DEV
const isTest = import.meta.env.MODE === 'test'

class IntercomService {
  private hasInitialized = false
  private unsubscribe?: () => void

  get skipRequest() {
    return isDevelopment || isTest || isPreviewMode()
  }

  init() {
    if (this.hasInitialized || this.skipRequest) return

    this.hasInitialized = true

    Intercom({ app_id: INTERCOM_APP_ID, region: INTERCOM_REGION })

    this.setupUserSubscription()
  }

  destroy() {
    this.unsubscribe?.()
    this.unsubscribe = undefined
    this.hasInitialized = false
    shutdown()
  }

  private setupUserSubscription() {
    const { user } = useAuthStore.getState()

    if (user) {
      this.identifyUser(user)
    }

    this.unsubscribe = useAuthStore.subscribe(({ user: currentUser }) => {
      if (currentUser) {
        this.identifyUser(currentUser)
      } else {
        shutdown()
      }
    })
  }

  private identifyUser(
    user: NonNullable<ReturnType<typeof useAuthStore.getState>['user']>
  ) {
    // intercomUserHash is null when INTERCOM_SECRET_KEY is not yet configured
    // on the environment — Intercom still works anonymously in that case.
    if (!user.intercomUserHash) return

    const createdAtTimestamp = user.createdAt
      ? Math.floor(new Date(user.createdAt).getTime() / 1000)
      : undefined

    update({
      name: user.fullName,
      email: user.email,
      user_hash: user.intercomUserHash,
      ...(createdAtTimestamp !== undefined && {
        created_at: createdAtTimestamp,
      }),
    })
  }
}

export const intercomService = new IntercomService()
